import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Spinner, Alert, Input,
    Modal, ModalHeader, ModalBody, ModalFooter, Progress,
    UncontrolledTooltip, Button, Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import classnames from "classnames";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── interfaces ── */
interface ContratInfo {
    id: number;
    ref: string;
    ligne_id: number;
    ligne_description: string;
    ligne_statut: number;
    ligne_statut_label?: string;
    fk_product: number;
}

interface ContactInfo {
    id: number;
    civility?: string;
    lastname?: string;
    firstname?: string;
    fullname?: string;
    poste?: string;
    email?: string;
    phone?: string;
    phone_mobile?: string;
    phone_perso?: string;
    fax?: string;
    address?: string;
    zip?: string;
    town?: string;
    statut?: number;
}

interface SimLine {
    idresource: number | null;
    resource: string;
    idsale: number;
    resource_type: string;
    wholesale: string;
    customer: string;
    retailer: string;
    date_save: string;
    idstatus: number;
    status_label: string;
    phone_line?: string;
    phone_line_formatted?: string;
    fk_socpeople?: number | null;
    contact?: ContactInfo | null;
    contrat: ContratInfo;
}

interface SaleDetail {
    idsale: number;
    reference: string;
    offer: string;
    product: string;
    product_state: string;
    wholesale: string;
    customer: string;
    engagement: string;
    amt_recur: number;
    amt_otc: number;
    date_begin: string;
    date_end: string;
    data_mo: number;
    data_go: number;
}

interface BalanceDetail {
    items: { name: string; unit: string; remain: number }[];
    total_mo: number;
    remain_mo: number;
    total_go: number;
    remain_go: number;
    pct: number;
}

interface ProductDetail {
    id: number;
    ref: string;
    label: string;
    description?: string;
    price: number;
    price_ttc?: number;
    tva_tx?: number;
    tosell?: number;
    tobuy?: number;
}

interface CharacteristicItem {
    id: number;
    name: string;
    value: string;
    date_save?: string;
}

interface LineDetails {
    sale: SaleDetail | null;
    balance: BalanceDetail;
    contrat: ContratInfo;
    product?: ProductDetail | null;
    characteristics?: CharacteristicItem[];
}

/* ── helpers ── */
const formatDate = (raw: string) => {
    if (!raw) return "—";
    // Format français "DD/MM/YYYY à HH:MM:SS"
    const frMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (frMatch) return `${frMatch[1]}/${frMatch[2]}/${frMatch[3]}`;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};

/* pct = % restant (API), usedPct = % consommé */
const progressColor = (usedPct: number) => {
    if (usedPct >= 75) return "danger";
    if (usedPct >= 50) return "warning";
    return "success";
};

/* ── CDR helpers ── */
interface CdrGroup {
    destination_label: string;
    nb_calls: number;
    total_billable_duration?: number;
    total_technical_duration: number; // secondes — durée technique (réelle)
    total_amount?: number;
}

/* Normalise un numéro vers le format `calling_identity_number` stocké en base
   au format international E.164 avec préfixe `+` (ex: +33610310473, +351912345678). */
const normalizeSda = (raw?: string | null): string | null => {
    if (!raw) return null;
    let d = String(raw).replace(/\D/g, "");
    if (!d) return null;
    if (d.startsWith("00")) d = d.slice(2);
    /* 10 chiffres commençant par 0 = numéro national français → préfixer 33 */
    if (d.length === 10 && d.startsWith("0")) d = "33" + d.slice(1);
    return "+" + d;
};

/* Période = mois en cours (du 1er au jour courant inclus) */
const currentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${d}` };
};

/* Formatte une durée en secondes -> "HH:MM:SS" (ex 969 -> "00:16:09") */
const formatDuration = (seconds: number): string => {
    const s = Math.max(0, Math.round(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

/* Type d'agrégation à afficher selon la destination */
type CdrUnit = "duration" | "sms" | "mms";
const getCdrUnit = (label?: string): CdrUnit => {
    const l = (label || "").toUpperCase();
    if (l.startsWith("SMS")) return "sms";
    if (l.startsWith("MMS")) return "mms";
    return "duration";
};

/* Valeur formatée affichée pour un groupe selon son unité */
const formatCdrGroupValue = (g: { destination_label: string; nb_calls: number; total_technical_duration: number; }): string => {
    const unit = getCdrUnit(g.destination_label);
    if (unit === "sms") return `${g.nb_calls} SMS`;
    if (unit === "mms") return `${g.nb_calls} MMS`;
    return formatDuration(g.total_technical_duration);
};

/* ── composant ── */
const FlotteMobile = () => {
    const [lines, setLines]     = useState<SimLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState("");
    const [balances, setBalances] = useState<Record<number, BalanceDetail>>({});
    /* CDR du mois en cours, indexé par SDA normalisé (ex "33612345678") */
    const [cdrBySda, setCdrBySda] = useState<Record<string, CdrGroup[]>>({});

    /* modal détail */
    const [modal, setModal]             = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail]           = useState<LineDetails | null>(null);
    const [selectedLine, setSelectedLine] = useState<SimLine | null>(null);

    /* modal contact (fiche) */
    const [modalContact, setModalContact] = useState(false);
    const [selectedContact, setSelectedContact] = useState<ContactInfo | null>(null);

    const openContact = (contact: ContactInfo) => {
        setSelectedContact(contact);
        setModalContact(true);
    };

    /* modal assignation utilisateur */
    const [modalAssign, setModalAssign] = useState(false);
    const [assignLine, setAssignLine] = useState<SimLine | null>(null);
    const [assignTab, setAssignTab] = useState<"select" | "create">("select");
    const [contactsList, setContactsList] = useState<ContactInfo[]>([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [contactsSearch, setContactsSearch] = useState("");
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    const [createForm, setCreateForm] = useState({
        civility: "", firstname: "", lastname: "",
        poste: "", email: "", phone: "", phone_mobile: "",
    });

    const openAssign = async (line: SimLine) => {
        setAssignLine(line);
        setAssignTab("select");
        setAssignError(null);
        setContactsSearch("");
        setCreateForm({ civility: "", firstname: "", lastname: "", poste: "", email: "", phone: "", phone_mobile: "" });
        setModalAssign(true);
        setContactsLoading(true);
        try {
            const res: any = await axios.post(
                "/apiV2/",
                { action: "consomobiles", subaction: "contacts" },
                apiV2PostConfig()
            );
            const list: ContactInfo[] = res?.data ?? res ?? [];
            setContactsList(Array.isArray(list) ? list : []);
        } catch {
            setContactsList([]);
        } finally {
            setContactsLoading(false);
        }
    };

    const applyAssignedContact = (idsale: number, contact: ContactInfo | null) => {
        setLines(prev => prev.map(l =>
            l.idsale === idsale
                ? { ...l, fk_socpeople: contact?.id ?? 0, contact: contact }
                : l
        ));
    };

    const assignContactToLine = async (contactId: number) => {
        if (!assignLine) return;
        setAssignLoading(true);
        setAssignError(null);
        try {
            const res: any = await axios.post(
                "/apiV2/",
                { action: "consomobiles", subaction: "setcontact", idsale: assignLine.idsale, fk_socpeople: contactId },
                apiV2PostConfig()
            );
            const payload = res?.data ?? res ?? {};
            const contact: ContactInfo | null = payload?.contact || contactsList.find(c => c.id === contactId) || null;
            applyAssignedContact(assignLine.idsale, contact);
            setModalAssign(false);
        } catch (e: any) {
            setAssignError(typeof e === "string" ? e : e?.message || "Erreur lors de l'assignation.");
        } finally {
            setAssignLoading(false);
        }
    };

    const submitCreateAndAssign = async () => {
        if (!assignLine) return;
        if (!createForm.firstname && !createForm.lastname) {
            setAssignError("Le prénom ou le nom est requis.");
            return;
        }
        setAssignLoading(true);
        setAssignError(null);
        try {
            const addRes: any = await axios.post(
                "/apiV2/",
                { action: "addcontact", ...createForm },
                apiV2PostConfig()
            );
            const addData = addRes?.data ?? addRes ?? {};
            const newContactId = addData?.data?.contact_id || addData?.contact_id;
            if (!newContactId) {
                setAssignError("Contact créé mais id introuvable.");
                return;
            }
            const setRes: any = await axios.post(
                "/apiV2/",
                { action: "consomobiles", subaction: "setcontact", idsale: assignLine.idsale, fk_socpeople: newContactId },
                apiV2PostConfig()
            );
            const setPayload = setRes?.data ?? setRes ?? {};
            applyAssignedContact(assignLine.idsale, setPayload?.contact || null);
            setModalAssign(false);
        } catch (e: any) {
            setAssignError(typeof e === "string" ? e : e?.message || "Erreur lors de la création.");
        } finally {
            setAssignLoading(false);
        }
    };

    const filteredContacts = useMemo(() => {
        const q = contactsSearch.toLowerCase();
        if (!q) return contactsList;
        return contactsList.filter(c =>
            (c.fullname || "").toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q) ||
            (c.phone || "").toLowerCase().includes(q) ||
            (c.phone_mobile || "").toLowerCase().includes(q)
        );
    }, [contactsList, contactsSearch]);

    /* ── fetch list + balances en parallèle ── */
    useEffect(() => {
        (async () => {
            try {
                const cfg = apiV2PostConfig();
                const res: any = await axios.post(
                    "/apiV2/",
                    { action: "consomobiles", subaction: "list" },
                    cfg
                );
                const list: SimLine[] = res?.data ?? res ?? [];
                setLines(list);
                setLoading(false);

                /* fetch toutes les balances en un seul appel batch */
                const batchRes: any = await axios.post(
                    "/apiV2/",
                    { action: "consomobiles", subaction: "batchbalance", idsales: list.map(l => l.idsale) },
                    cfg
                );
                const batchData: Record<string, BalanceDetail> = batchRes?.data ?? batchRes ?? {};
                const map: Record<number, BalanceDetail> = {};
                list.forEach(l => {
                    const b = batchData[String(l.idsale)];
                    if (b && (b.total_mo > 0 || b.total_go > 0)) map[l.idsale] = b;
                });
                setBalances(map);

                /* fetch CDR du mois en cours, groupés par destination, en 1 seul appel batch */
                const sdas = Array.from(
                    new Set(
                        list
                            .map(l => normalizeSda(l.phone_line || l.phone_line_formatted))
                            .filter((s): s is string => !!s)
                    )
                );
                if (sdas.length > 0) {
                    const { from, to } = currentMonthRange();
                    try {
                        const cdrRes: any = await axios.post(
                            "/apiV2/",
                            {
                                action: "cdr",
                                subaction: "batchgroupby",
                                sdas,
                                from,
                                to,
                                exclude: "Data",
                            },
                            cfg
                        );
                        const cdrData: Record<string, CdrGroup[]> = cdrRes?.data?.data ?? cdrRes?.data ?? {};
                        setCdrBySda(cdrData);
                    } catch {
                        /* CDR indisponibles, on laisse les colonnes vides */
                    }
                }
            } catch {
                setLoading(false);
            }
        })();
    }, []);

    /* ── fetch détail ligne ── */
    const openDetail = async (line: SimLine) => {
        setSelectedLine(line);
        setDetail(null);
        setModal(true);
        setDetailLoading(true);
        try {
            const res: any = await axios.post(
                "/apiV2/",
                { action: "consomobiles", subaction: "details", idsale: line.idsale },
                apiV2PostConfig()
            );
            const detailData = res?.data ?? res ?? null;
            setDetail(detailData);
        } catch {
            /* détail non disponible, modal reste ouvert avec info de base */
        } finally {
            setDetailLoading(false);
        }
    };

    /* ── KPI ── */
    const total    = lines.length;
    const actives  = lines.filter(x => x.contrat?.ligne_statut === 4).length;
    const inactifs = lines.filter(x => x.contrat?.ligne_statut !== 4).length;
    const operateurs = new Set(lines.map(x => x.wholesale).filter(Boolean)).size;

    const widgets = [
        { label: "Total lignes",    counter: total,     icon: "ri-sim-card-line",          color: "primary" },
        { label: "Actives",         counter: actives,   icon: "ri-checkbox-circle-line",   color: "success" },
        { label: "Inactives",       counter: inactifs,  icon: "ri-close-circle-line",      color: "danger"  },
        { label: "Réseaux",          counter: operateurs,icon: "ri-broadcast-line",         color: "info"    },
    ];

    /* ── filtre ── */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return lines;
        return lines.filter(x =>
            (x.resource || "").toLowerCase().includes(q) ||
            (x.phone_line_formatted || "").toLowerCase().includes(q) ||
            (x.phone_line || "").toLowerCase().includes(q) ||
            (x.wholesale || "").toLowerCase().includes(q) ||
            (x.contact?.fullname || "").toLowerCase().includes(q) ||
            (x.customer || "").toLowerCase().includes(q) ||
            (x.contrat?.ref || "").toLowerCase().includes(q)
        );
    }, [lines, search]);

    /* ── loading ── */
    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                    <p className="text-muted mt-3 fw-medium">Chargement de la flotte mobile…</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Gestion de flotte" pageTitle="Espace Client" />

                    {/* ── Widgets KPI ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl={3} sm={6} key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp start={0} end={w.counter} duration={2} />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Tableau ── */}
                    <Row>
                        <Col lg={12}>
                            <Card id="flotteList">
                                <CardHeader className="border-0">
                                    <div className="d-flex align-items-center">
                                        <h5 className="card-title mb-0 flex-grow-1">Lignes SIM / eSIM</h5>
                                        <div className="search-box">
                                            <Input
                                                type="text"
                                                className="form-control search"
                                                placeholder="Rechercher un numéro, opérateur, contrat…"
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                                style={{ minWidth: 280 }}
                                            />
                                            <i className="ri-search-line search-icon"></i>
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardBody className="pt-0">
                                    {filtered.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                    <i className="ri-sim-card-line"></i>
                                                </span>
                                            </div>
                                            <h5 className="mt-2">Aucune ligne trouvée</h5>
                                            <p className="text-muted">
                                                {search ? "Aucun résultat pour cette recherche." : "Aucune ligne SIM / eSIM associée à votre compte."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive table-card mb-1">
                                            <table className="table align-middle table-nowrap mb-0">
                                                <thead className="table-light text-muted">
                                                    <tr>
                                                        <th>Numéro</th>
                                                        <th>Utilisateur</th>
                                                        <th>Réseau</th>
                                                        <th>Contrat</th>
                                                        <th>Date activation</th>
                                                        <th>Consommation</th>
                                                        <th>Conso voix (mois)</th>
                                                        <th className="text-center">Statut</th>
                                                        <th className="text-center">Détails</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered.map((line, idx) => {
                                                        const hasContact = !!(line.fk_socpeople && line.fk_socpeople > 0 && line.contact);
                                                        return (
                                                        <tr key={idx}>
                                                            <td>
                                                                <div
                                                                    id={`tooltip-line-${line.idsale}`}
                                                                    className="d-flex align-items-center gap-2"
                                                                >
                                                                    <div className="avatar-xs flex-shrink-0">
                                                                        <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-14">
                                                                            <i className="ri-sim-card-2-line"></i>
                                                                        </span>
                                                                    </div>
                                                                    <span className="fw-semibold">{line.phone_line_formatted || line.phone_line || "—"}</span>
                                                                </div>
                                                                {line.contrat?.ligne_description && (
                                                                    <UncontrolledTooltip placement="top" target={`tooltip-line-${line.idsale}`}>
                                                                        <span dangerouslySetInnerHTML={{ __html: line.contrat.ligne_description }} />
                                                                    </UncontrolledTooltip>
                                                                )}
                                                            </td>
                                                            <td className="fs-13">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    {hasContact ? (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0 fs-13 text-decoration-none"
                                                                            onClick={() => openContact(line.contact!)}
                                                                            title="Voir la fiche contact"
                                                                        >
                                                                            <i className="ri-user-line me-1"></i>
                                                                            {line.contact!.fullname || "—"}
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-muted">{line.customer || "—"}</span>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link btn-sm p-0 text-muted ms-1"
                                                                        onClick={() => openAssign(line)}
                                                                        title="Modifier l'utilisateur"
                                                                    >
                                                                        <i className="ri-pencil-line"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-info-subtle text-info fs-12">
                                                                    {line.wholesale || "—"}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="fw-medium link-primary fs-13">
                                                                    {line.contrat?.ref || "—"}
                                                                </span>
                                                            </td>
                                                            <td className="text-muted fs-13">
                                                                {formatDate(line.date_save)}
                                                            </td>
                                                            <td style={{ minWidth: 180 }}>
                                                                {(() => {
                                                                    const b = balances[line.idsale];
                                                                    if (!b) return <span className="text-muted fs-12">—</span>;
                                                                    const usedPct = 100 - b.pct;
                                                                    const label = b.total_go > 0
                                                                        ? `${b.total_go - b.remain_go} / ${b.total_go} Go`
                                                                        : `${Math.round(b.total_mo - b.remain_mo)} / ${b.total_mo} Mo`;
                                                                    return (
                                                                        <div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span className="fs-12 text-muted">{label}</span>
                                                                                <span className="fs-12 text-muted">{usedPct}%</span>
                                                                            </div>
                                                                            <Progress
                                                                                value={usedPct}
                                                                                color={progressColor(usedPct)}
                                                                                style={{ height: 6, borderRadius: 4 }}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td style={{ minWidth: 180 }}>
                                                                {(() => {
                                                                    const sda = normalizeSda(line.phone_line || line.phone_line_formatted);
                                                                    if (!sda) return <span className="text-muted fs-12">—</span>;
                                                                    const groups = cdrBySda[sda];
                                                                    if (!groups || groups.length === 0) {
                                                                        return <span className="text-muted fs-12">Aucun appel</span>;
                                                                    }
                                                                    /* Voix : somme des durées (hors SMS/MMS) */
                                                                    const totalSec = groups
                                                                        .filter((g) => getCdrUnit(g.destination_label) === "duration")
                                                                        .reduce((sum, g) => sum + (g.total_technical_duration || 0), 0);
                                                                    /* SMS / MMS : somme des nb_calls */
                                                                    const totalSms = groups
                                                                        .filter((g) => getCdrUnit(g.destination_label) === "sms")
                                                                        .reduce((sum, g) => sum + (g.nb_calls || 0), 0);
                                                                    const totalMms = groups
                                                                        .filter((g) => getCdrUnit(g.destination_label) === "mms")
                                                                        .reduce((sum, g) => sum + (g.nb_calls || 0), 0);
                                                                    const tipId = `cdr-tip-${line.idsale}`;
                                                                    return (
                                                                        <>
                                                                            <div id={tipId}>
                                                                                {totalSec > 0 && (
                                                                                    <div className="d-flex align-items-center">
                                                                                        <i className="ri-time-line text-primary me-1"></i>
                                                                                        <span className="fw-semibold fs-13">{formatDuration(totalSec)}</span>
                                                                                    </div>
                                                                                )}
                                                                                {(totalSms > 0 || totalMms > 0) && (
                                                                                    <div className="d-flex align-items-center text-muted fs-12">
                                                                                        <i className="ri-message-2-line me-1"></i>
                                                                                        {totalSms > 0 && <span className="me-2">{totalSms} SMS</span>}
                                                                                        {totalMms > 0 && <span>{totalMms} MMS</span>}
                                                                                    </div>
                                                                                )}
                                                                                {totalSec === 0 && totalSms === 0 && totalMms === 0 && (
                                                                                    <span className="text-muted fs-12">Aucun appel</span>
                                                                                )}
                                                                            </div>
                                                                            <UncontrolledTooltip placement="left" target={tipId} autohide={false}>
                                                                                <div className="text-start">
                                                                                    <div className="fw-semibold mb-1">Détail par destination</div>
                                                                                    {groups.map((g, j) => (
                                                                                        <div key={j} className="d-flex justify-content-between gap-3">
                                                                                            <span>{g.destination_label || "—"}</span>
                                                                                            <span className="fw-semibold">{formatCdrGroupValue(g)}</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </UncontrolledTooltip>
                                                                        </>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="text-center">
                                                                {line.contrat?.ligne_statut === 4 ? (
                                                                    <Badge className="bg-success-subtle text-success text-uppercase">
                                                                        <i className="ri-checkbox-circle-fill me-1"></i>
                                                                        {line.contrat?.ligne_statut_label || "Actif"}
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge className="bg-danger-subtle text-danger text-uppercase">
                                                                        <i className="ri-close-circle-fill me-1"></i>
                                                                        {line.contrat?.ligne_statut_label || "Inactif"}
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    className="btn btn-soft-primary btn-sm"
                                                                    onClick={() => openDetail(line)}
                                                                    title="Voir les détails"
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                </Container>
            </div>

            {/* ── Modal détail ligne ── */}
            <Modal isOpen={modal} toggle={() => setModal(false)} size="md" centered>
                <ModalHeader toggle={() => setModal(false)} className="border-bottom-dashed">
                    <div className="d-flex align-items-center gap-2">
                        <span className="avatar-xs">
                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                <i className="ri-sim-card-2-line"></i>
                            </span>
                        </span>
                        <span className="fw-semibold fs-15">
                            {selectedLine?.phone_line_formatted || selectedLine?.phone_line || selectedLine?.resource || "Détails de la ligne"}
                        </span>
                        {selectedLine && (
                            <Badge className="bg-info-subtle text-info ms-1">
                                {selectedLine.wholesale}
                            </Badge>
                        )}
                    </div>
                </ModalHeader>
                <ModalBody className="p-4">
                    {detailLoading ? (
                        <div className="text-center py-4">
                            <Spinner style={{ color: "#0066A1" }} />
                            <p className="text-muted mt-2">Chargement des détails…</p>
                        </div>
                    ) : detail ? (
                        <div className="vstack gap-3">

                            {/* ── Conso data ── */}
                            {detail.balance && detail.balance.total_mo > 0 && (() => {
                                const usedPct = 100 - detail.balance.pct;
                                return (
                                    <Card className="border bg-light mb-0">
                                        <CardBody className="p-3">
                                            <h6 className="text-uppercase text-muted fs-11 mb-3">
                                                <i className="ri-signal-wifi-line me-1"></i>Consommation Data
                                            </h6>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="fw-semibold fs-14">
                                                    {detail.balance.total_go > 0
                                                        ? `${detail.balance.total_go - detail.balance.remain_go} Go utilisés`
                                                        : `${Math.round(detail.balance.total_mo - detail.balance.remain_mo)} Mo utilisés`}
                                                </span>
                                                <span className="text-muted fs-13">
                                                    / {detail.balance.total_go > 0
                                                        ? `${detail.balance.total_go} Go`
                                                        : `${detail.balance.total_mo} Mo`}
                                                </span>
                                            </div>
                                            <Progress
                                                value={usedPct}
                                                color={progressColor(usedPct)}
                                                style={{ height: 10, borderRadius: 8 }}
                                            />
                                            <div className="text-end mt-1">
                                                <small className="text-muted">{usedPct}% utilisé</small>
                                            </div>
                                        </CardBody>
                                    </Card>
                                );
                            })()}

                            {/* ── Infos ligne ── */}
                            <table className="table table-borderless table-sm mb-0">
                                <tbody>
                                    {(detail.product?.label || detail.sale?.offer) && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal" style={{ width: "40%" }}>Offre</th>
                                            <td className="fw-medium">{detail.product?.label || detail.sale?.offer}</td>
                                        </tr>
                                    )}
                                    {(detail.product?.ref || detail.sale?.reference) && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal">Référence</th>
                                            <td className="fw-medium">{detail.product?.ref || detail.sale?.reference}</td>
                                        </tr>
                                    )}
                                    {(detail.sale?.data_go ?? 0) > 0 && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal">Forfait data</th>
                                            <td className="fw-medium">{detail.sale!.data_go} Go</td>
                                        </tr>
                                    )}
                                    {((detail.product?.price ?? 0) > 0 || (detail.sale?.amt_recur ?? 0) > 0) && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal">Mensualité</th>
                                            <td className="fw-medium">
                                                {((detail.product?.price ?? detail.sale?.amt_recur ?? 0)).toFixed(2)} €
                                            </td>
                                        </tr>
                                    )}
                                    {detail.contrat?.ref && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal">Contrat</th>
                                            <td className="fw-medium link-primary">{detail.contrat.ref}</td>
                                        </tr>
                                    )}
                                    {(() => {
                                        const sim = detail.characteristics?.find(
                                            (c) => (c.name || "").toUpperCase() === "SIM"
                                        )?.value;
                                        return sim ? (
                                            <tr>
                                                <th className="ps-0 text-muted fw-normal">SIM</th>
                                                <td className="fw-medium font-monospace">{sim}</td>
                                            </tr>
                                        ) : null;
                                    })()}
                                </tbody>
                            </table>

                        </div>
                    ) : (
                        /* fallback : info de base depuis la liste */
                        selectedLine && (
                            <Row className="g-3">
                                <Col xs={12}>
                                    <Alert color="warning" className="mb-0">
                                        <i className="ri-information-line me-2"></i>
                                        Les détails complets ne sont pas disponibles pour cette ligne.
                                    </Alert>
                                </Col>
                                <Col md={6}>
                                    <p className="text-muted mb-1 fs-12">Numéro</p>
                                    <p className="fw-semibold">{selectedLine.phone_line_formatted || selectedLine.phone_line || selectedLine.resource}</p>
                                </Col>
                                <Col md={6}>
                                    <p className="text-muted mb-1 fs-12">Réseau</p>
                                    <p className="fw-semibold">{selectedLine.wholesale}</p>
                                </Col>
                                <Col md={6}>
                                    <p className="text-muted mb-1 fs-12">Type</p>
                                    <p className="fw-semibold">{selectedLine.resource_type}</p>
                                </Col>
                                <Col md={6}>
                                    <p className="text-muted mb-1 fs-12">Contrat</p>
                                    <p className="fw-semibold">{selectedLine.contrat?.ref}</p>
                                </Col>
                            </Row>
                        )
                    )}
                </ModalBody>
            </Modal>

            {/* ── Modal Contact (sm) ── */}
            <Modal isOpen={modalContact} toggle={() => setModalContact(false)} size="sm" centered>
                <ModalHeader toggle={() => setModalContact(false)} className="border-bottom-dashed">
                    <div className="d-flex align-items-center gap-2">
                        <span className="avatar-xs">
                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                <i className="ri-user-line"></i>
                            </span>
                        </span>
                        <span className="fw-semibold fs-15">Fiche contact</span>
                    </div>
                </ModalHeader>
                <ModalBody className="p-3">
                    {selectedContact ? (
                        <div className="vstack gap-2">
                            <div className="text-center mb-2">
                                <div className="avatar-md mx-auto mb-2">
                                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3 fw-bold">
                                        {((selectedContact.firstname?.[0] || "") + (selectedContact.lastname?.[0] || "")).toUpperCase() || "?"}
                                    </span>
                                </div>
                                <h6 className="mb-0">{selectedContact.fullname || "—"}</h6>
                                {selectedContact.poste && (
                                    <p className="text-muted fs-12 mb-0">{selectedContact.poste}</p>
                                )}
                            </div>
                            <table className="table table-borderless table-sm mb-0">
                                <tbody>
                                    {selectedContact.email && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal fs-12" style={{ width: "32%" }}>
                                                <i className="ri-mail-line me-1"></i>Email
                                            </th>
                                            <td className="fs-13">
                                                <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                                            </td>
                                        </tr>
                                    )}
                                    {selectedContact.phone && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal fs-12">
                                                <i className="ri-phone-line me-1"></i>Tél.
                                            </th>
                                            <td className="fs-13">{selectedContact.phone}</td>
                                        </tr>
                                    )}
                                    {selectedContact.phone_mobile && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal fs-12">
                                                <i className="ri-smartphone-line me-1"></i>Mobile
                                            </th>
                                            <td className="fs-13">{selectedContact.phone_mobile}</td>
                                        </tr>
                                    )}
                                    {(selectedContact.address || selectedContact.zip || selectedContact.town) && (
                                        <tr>
                                            <th className="ps-0 text-muted fw-normal fs-12">
                                                <i className="ri-map-pin-line me-1"></i>Adresse
                                            </th>
                                            <td className="fs-13">
                                                {selectedContact.address && <div>{selectedContact.address}</div>}
                                                {(selectedContact.zip || selectedContact.town) && (
                                                    <div>{[selectedContact.zip, selectedContact.town].filter(Boolean).join(" ")}</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted text-center mb-0">Aucune information de contact.</p>
                    )}
                </ModalBody>
            </Modal>

            {/* ── Modal : Modifier / assigner un utilisateur ── */}
            <Modal isOpen={modalAssign} toggle={() => setModalAssign(false)} size="md" centered>
                <ModalHeader toggle={() => setModalAssign(false)} className="border-bottom-dashed">
                    <div className="d-flex align-items-center gap-2">
                        <span className="avatar-xs">
                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                <i className="ri-user-settings-line"></i>
                            </span>
                        </span>
                        <span className="fw-semibold fs-15">
                            Modifier l'utilisateur
                            {assignLine && (
                                <span className="text-muted fs-13 ms-2">
                                    ({assignLine.phone_line_formatted || assignLine.phone_line || "—"})
                                </span>
                            )}
                        </span>
                    </div>
                </ModalHeader>
                <ModalBody className="p-0">
                    <Nav tabs className="nav-tabs-custom nav-primary mb-0 px-3 pt-2">
                        <NavItem>
                            <NavLink
                                className={classnames({ active: assignTab === "select" })}
                                onClick={() => setAssignTab("select")}
                                style={{ cursor: "pointer" }}
                            >
                                <i className="ri-list-check me-1"></i>Sélectionner
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: assignTab === "create" })}
                                onClick={() => setAssignTab("create")}
                                style={{ cursor: "pointer" }}
                            >
                                <i className="ri-user-add-line me-1"></i>Nouveau contact
                            </NavLink>
                        </NavItem>
                    </Nav>

                    {assignError && (
                        <Alert color="danger" className="m-3 mb-0">
                            <i className="ri-error-warning-line me-2"></i>{assignError}
                        </Alert>
                    )}

                    <TabContent activeTab={assignTab} className="p-3">
                        {/* ── Tab Sélectionner ── */}
                        <TabPane tabId="select">
                            <Input
                                type="text"
                                placeholder="Rechercher un contact…"
                                value={contactsSearch}
                                onChange={e => setContactsSearch(e.target.value)}
                                className="mb-2"
                            />
                            {contactsLoading ? (
                                <div className="text-center py-4">
                                    <Spinner style={{ color: "#0066A1" }} />
                                </div>
                            ) : filteredContacts.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="ri-user-line fs-2 mb-2 d-block"></i>
                                    Aucun contact trouvé.
                                </div>
                            ) : (
                                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                                    <table className="table table-hover align-middle mb-0">
                                        <tbody>
                                            {(assignLine?.fk_socpeople ?? 0) > 0 ? (
                                                <tr>
                                                    <td colSpan={2}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm w-100"
                                                            onClick={() => assignContactToLine(0)}
                                                            disabled={assignLoading}
                                                        >
                                                            <i className="ri-close-circle-line me-1"></i>
                                                            Retirer le contact actuel
                                                        </button>
                                                    </td>
                                                </tr>
                                            ) : null}
                                            {filteredContacts.map(c => {
                                                const isCurrent = assignLine?.fk_socpeople === c.id;
                                                return (
                                                    <tr
                                                        key={c.id}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => !assignLoading && assignContactToLine(c.id)}
                                                        className={isCurrent ? "table-active" : ""}
                                                    >
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar-xs flex-shrink-0">
                                                                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-14 fw-semibold">
                                                                        {((c.firstname?.[0] || "") + (c.lastname?.[0] || "")).toUpperCase() || "?"}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-medium">{c.fullname || "—"}</div>
                                                                    {c.email && <div className="text-muted fs-12">{c.email}</div>}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-end">
                                                            {isCurrent && (
                                                                <Badge className="bg-success-subtle text-success">
                                                                    <i className="ri-check-line me-1"></i>Actuel
                                                                </Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </TabPane>

                        {/* ── Tab Créer ── */}
                        <TabPane tabId="create">
                            <Row className="g-3">
                                <Col xs={4}>
                                    <label className="form-label text-muted fw-medium fs-13">Civilité</label>
                                    <select
                                        className="form-select"
                                        value={createForm.civility}
                                        onChange={e => setCreateForm(f => ({ ...f, civility: e.target.value }))}
                                    >
                                        <option value="">—</option>
                                        <option value="MR">M.</option>
                                        <option value="MME">Mme</option>
                                    </select>
                                </Col>
                                <Col xs={8}>
                                    <label className="form-label text-muted fw-medium fs-13">Prénom</label>
                                    <input
                                        type="text" className="form-control"
                                        value={createForm.firstname}
                                        onChange={e => setCreateForm(f => ({ ...f, firstname: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label text-muted fw-medium fs-13">Nom <span className="text-danger">*</span></label>
                                    <input
                                        type="text" className="form-control"
                                        value={createForm.lastname}
                                        onChange={e => setCreateForm(f => ({ ...f, lastname: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label text-muted fw-medium fs-13">Poste</label>
                                    <input
                                        type="text" className="form-control"
                                        value={createForm.poste}
                                        onChange={e => setCreateForm(f => ({ ...f, poste: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label text-muted fw-medium fs-13">Email</label>
                                    <input
                                        type="email" className="form-control"
                                        value={createForm.email}
                                        onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={6}>
                                    <label className="form-label text-muted fw-medium fs-13">Téléphone</label>
                                    <input
                                        type="text" className="form-control"
                                        value={createForm.phone}
                                        onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={6}>
                                    <label className="form-label text-muted fw-medium fs-13">Mobile</label>
                                    <input
                                        type="text" className="form-control"
                                        value={createForm.phone_mobile}
                                        onChange={e => setCreateForm(f => ({ ...f, phone_mobile: e.target.value }))}
                                    />
                                </Col>
                            </Row>
                        </TabPane>
                    </TabContent>
                </ModalBody>
                <ModalFooter className="border-top-dashed">
                    <Button color="light" onClick={() => setModalAssign(false)} disabled={assignLoading}>
                        Annuler
                    </Button>
                    {assignTab === "create" && (
                        <Button color="primary" onClick={submitCreateAndAssign} disabled={assignLoading}>
                            {assignLoading
                                ? <><Spinner size="sm" className="me-1" />Création…</>
                                : <><i className="ri-save-line me-1"></i>Créer et assigner</>
                            }
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

export default FlotteMobile;
